import {
  type Option,
  type FormatOptionLabelString,
  SortOrder,
  type FormatOptionLabelReactNode,
} from '../../Components/SelectionList/index.ts';
import { COMMA } from './constants.ts';
import { type GetOptionText } from './types.ts';

export const areOptionsEqual = <T>(a: Option<T>, b: Option<T>): boolean => a.value === b.value;

export const getOptionLabel = <T>(
  option: Option<T>,
  formatOptionLabel: FormatOptionLabelString<T> | FormatOptionLabelReactNode<T> | undefined,
) => formatOptionLabel?.(option) ?? option.label;

export const getOptionText = <T>(
  option: Option<T>,
  formatOptionLabel: FormatOptionLabelString<T> | FormatOptionLabelReactNode<T> | undefined,
  getOptionTextFn: GetOptionText<T> | undefined,
): string => {
  if (getOptionTextFn) {
    return getOptionTextFn(option);
  }
  // If formatOptionLabel returns a string, use it; otherwise fall back to option.label
  const formatted = formatOptionLabel?.(option);
  return typeof formatted === 'string' ? formatted : option.label;
};

export const createOptionValueSet = <T>(options: Option<T>[]): Set<T> => new Set(options.map((option) => option.value));

export const filterOptionsExcluding = <T>(options: Option<T>[], excludeSet: Set<T>): Option<T>[] =>
  options.filter((option) => !excludeSet.has(option.value));

export const filterOptionsByString = <T>(
  options: Option<T>[],
  searchString: string,
  formatOptionLabel: FormatOptionLabelString<T> | FormatOptionLabelReactNode<T> | undefined,
  getOptionTextFn?: GetOptionText<T>,
): Option<T>[] => {
  if (!options.length) {
    return [];
  }

  if (!searchString.trim()) {
    return options;
  }

  const searchTerms = searchString
    .toLowerCase()
    .split(COMMA)
    .map((term) => term.trim())
    .filter((term) => term.length > 0);

  return options.filter((option) => {
    const textContent = getOptionText(option, formatOptionLabel, getOptionTextFn).toLowerCase();
    return searchTerms.some((term) => textContent.includes(term));
  });
};

export const sortOptions = <T>(
  options: Option<T>[],
  order: SortOrder,
  formatOptionLabel: FormatOptionLabelString<T> | FormatOptionLabelReactNode<T> | undefined,
  getOptionTextFn?: GetOptionText<T>,
): Option<T>[] => {
  if (!options?.length) {
    return [];
  }
  if (order === SortOrder.ASC) {
    return [...options].sort((a, b) =>
      getOptionText(a, formatOptionLabel, getOptionTextFn).localeCompare(getOptionText(b, formatOptionLabel, getOptionTextFn)),
    );
  }
  return [...options].sort((a, b) =>
    getOptionText(b, formatOptionLabel, getOptionTextFn).localeCompare(getOptionText(a, formatOptionLabel, getOptionTextFn)),
  );
};
